import AppIconButton from "../../components/form/AppIconButton";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import ApiOutlinedIcon from "@mui/icons-material/ApiOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import ConfiguratorRoot from "./ConfiguratorRoot";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";
import { useMaterialUIController, setOpenConfigurator, setFixedNavbar } from "../../context";
import { useTranslation } from "react-i18next";
import { AppButton, AppTextField } from "../../components/form";
import {
  accountService,
  type AccountProfile,
} from "../../services/accountService";
import authService from "../../services/authService";
import { REACT_APP_BASE_PATH } from "../../config/runtimeConfig";

/** Short build identifier shown at the bottom of the panel (`vite.config.ts` injects the package version). */
const APP_VERSION = typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "dev";
const swaggerUiUrl = `${(REACT_APP_BASE_PATH || "").replace(/\/+$/, "")}/swagger-ui/index.html`;

/**
 * Layout preferences plus the signed-in user's profile and password.
 * Language and theme live in the navbar so they are one click away.
 */
function Configurator() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const custom = useAppCustomStyles();
  const configurator = custom.widgets.configurator;
  const [controller, dispatch] = useMaterialUIController();
  const { openConfigurator, fixedNavbar } = controller;

  const [profile, setProfile] = useState<AccountProfile | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileEditing, setProfileEditing] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordEditing, setPasswordEditing] = useState(false);

  const handleCloseConfigurator = () => setOpenConfigurator(dispatch, false);
  const handleFixedNavbar = () => setFixedNavbar(dispatch, !fixedNavbar);

  const loadProfile = useCallback(async () => {
    setProfileLoading(true);
    setProfileError(null);
    try {
      const data = await accountService.getProfile();
      setProfile(data);
      setFirstName(data.firstName ?? "");
      setLastName(data.lastName ?? "");
    } catch (e: unknown) {
      setProfileError(e instanceof Error ? e.message : String(e));
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!openConfigurator) return;
    setProfileMessage(null);
    setProfileError(null);
    setPasswordError(null);
    setProfileEditing(false);
    setPasswordEditing(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    void loadProfile();
  }, [openConfigurator, loadProfile]);

  const startProfileEdit = () => {
    setProfileMessage(null);
    setProfileError(null);
    setFirstName(profile?.firstName ?? "");
    setLastName(profile?.lastName ?? "");
    setProfileEditing(true);
  };

  const cancelProfileEdit = () => {
    setFirstName(profile?.firstName ?? "");
    setLastName(profile?.lastName ?? "");
    setProfileError(null);
    setProfileEditing(false);
  };

  const saveProfile = async () => {
    setProfileSaving(true);
    setProfileMessage(null);
    setProfileError(null);
    try {
      const updated = await accountService.updateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      setProfile(updated);
      setFirstName(updated.firstName ?? "");
      setLastName(updated.lastName ?? "");
      setProfileEditing(false);
      setProfileMessage(t("configurator.profileSaved"));
    } catch (e: unknown) {
      setProfileError(e instanceof Error ? e.message : String(e));
    } finally {
      setProfileSaving(false);
    }
  };

  const startPasswordEdit = () => {
    setPasswordError(null);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordEditing(true);
  };

  const cancelPasswordEdit = () => {
    setPasswordError(null);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordEditing(false);
  };

  const changePassword = async () => {
    setPasswordError(null);
    if (!currentPassword || !newPassword) {
      setPasswordError(t("configurator.passwordRequired"));
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError(t("configurator.passwordTooShort"));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(t("configurator.passwordMismatch"));
      return;
    }
    setPasswordSaving(true);
    try {
      await accountService.changePassword({ currentPassword, newPassword });
      handleCloseConfigurator();
      await authService.logout();
    } catch (e: unknown) {
      setPasswordError(e instanceof Error ? e.message : String(e));
      setPasswordSaving(false);
    }
  };

  return (
    <>
      <Backdrop
        open={openConfigurator}
        onClick={handleCloseConfigurator}
        sx={configurator.backdrop}
      />

      <ConfiguratorRoot openConfigurator={openConfigurator}>
        <Box
          color="text"
          display="flex"
          justifyContent="space-between"
          alignItems="baseline"
          pt={4}
          pb={0.5}
          px={3}
        >
          <Box color="inherit">
            <Typography variant="h5">{t("configurator.title")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("configurator.subtitle")}
            </Typography>
          </Box>

          <AppIconButton
            size="small"
            onClick={handleCloseConfigurator}
            aria-label={t("configurator.close")}
            sx={configurator.headerCloseButton}
          >
            <CloseIcon />
          </AppIconButton>
        </Box>

        <Divider sx={configurator.divider} />

        <Box color="text" pt={0.5} pb={3} px={3}>
          <Typography variant="h6" mt={1} mb={1.5}>
            {t("configurator.profileTitle")}
          </Typography>
          {profileLoading && (
            <Typography variant="body2" color="text.secondary" mb={1}>
              {t("configurator.profileLoading")}
            </Typography>
          )}
          {profileError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {profileError}
            </Alert>
          )}
          {profileMessage && (
            <Alert severity="success" sx={{ mb: 1.5 }}>
              {profileMessage}
            </Alert>
          )}
          <AppTextField
            label={t("configurator.username")}
            value={profile?.username ?? ""}
            fullWidth
            disabled
            sx={{ mb: 1.5 }}
          />
          <AppTextField
            label={t("configurator.email")}
            value={profile?.email ?? ""}
            fullWidth
            disabled
            helperText={t("configurator.emailReadOnly")}
            sx={{ mb: 1.5 }}
          />
          <AppTextField
            label={t("configurator.firstName")}
            value={profileEditing ? firstName : (profile?.firstName ?? "")}
            onChange={(e) => setFirstName(e.target.value)}
            fullWidth
            disabled={!profileEditing || profileLoading || profileSaving}
            sx={{ mb: 1.5 }}
          />
          <AppTextField
            label={t("configurator.lastName")}
            value={profileEditing ? lastName : (profile?.lastName ?? "")}
            onChange={(e) => setLastName(e.target.value)}
            fullWidth
            disabled={!profileEditing || profileLoading || profileSaving}
            sx={{ mb: 1.5 }}
          />
          {profileEditing ? (
            <Box sx={{ display: "flex", gap: 1 }}>
              <AppButton
                variant="outlined"
                onClick={cancelProfileEdit}
                disabled={profileSaving}
                fullWidth
              >
                {t("common.cancel")}
              </AppButton>
              <AppButton
                variant="contained"
                onClick={saveProfile}
                disabled={profileLoading || profileSaving}
                fullWidth
              >
                {profileSaving ? t("configurator.saving") : t("configurator.saveProfile")}
              </AppButton>
            </Box>
          ) : (
            <AppButton
              variant="outlined"
              onClick={startProfileEdit}
              disabled={profileLoading}
              fullWidth
            >
              {t("configurator.editProfile")}
            </AppButton>
          )}

          <Divider sx={{ ...configurator.divider, my: 2.5 }} />

          <Typography variant="h6" mb={1.5}>
            {t("configurator.passwordTitle")}
          </Typography>
          {!passwordEditing ? (
            <AppButton
              variant="outlined"
              color="warning"
              onClick={startPasswordEdit}
              fullWidth
            >
              {t("configurator.changePassword")}
            </AppButton>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" mb={1.5}>
                {t("configurator.passwordHelp")}
              </Typography>
              {passwordError && (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                  {passwordError}
                </Alert>
              )}
              <AppTextField
                label={t("configurator.currentPassword")}
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                fullWidth
                disabled={passwordSaving}
                sx={{ mb: 1.5 }}
              />
              <AppTextField
                label={t("configurator.newPassword")}
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
                disabled={passwordSaving}
                sx={{ mb: 1.5 }}
              />
              <AppTextField
                label={t("configurator.confirmPassword")}
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                fullWidth
                disabled={passwordSaving}
                sx={{ mb: 1.5 }}
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <AppButton
                  variant="outlined"
                  onClick={cancelPasswordEdit}
                  disabled={passwordSaving}
                  fullWidth
                >
                  {t("common.cancel")}
                </AppButton>
                <AppButton
                  variant="contained"
                  color="warning"
                  onClick={changePassword}
                  disabled={passwordSaving}
                  fullWidth
                >
                  {passwordSaving
                    ? t("configurator.changingPassword")
                    : t("configurator.savePassword")}
                </AppButton>
              </Box>
            </>
          )}

          <Divider sx={{ ...configurator.divider, my: 2.5 }} />

          <Typography variant="h6" mb={1}>
            {t("configurator.developerTitle")}
          </Typography>
          <AppButton
            variant="outlined"
            fullWidth
            startIcon={<ApiOutlinedIcon />}
            busyIgnore
            onClick={() => {
              window.open(swaggerUiUrl, "_blank", "noopener,noreferrer");
            }}
            sx={{ mb: 0.5 }}
          >
            {t("configurator.openApi")}
          </AppButton>

          <Divider sx={{ ...configurator.divider, my: 2 }} />

          <Box display="flex" justifyContent="space-between" alignItems="center" lineHeight={1}>
            <Typography variant="h6" id="configurator-navbar-fixed">
              {t("configurator.navbarFixed")}
            </Typography>

            <Switch
              checked={fixedNavbar}
              onChange={handleFixedNavbar}
              color="primary"
              sx={configurator.switch}
              slotProps={{ input: { "aria-labelledby": "configurator-navbar-fixed" } }}
            />
          </Box>
          <Divider sx={{ ...configurator.divider, my: 2 }} />
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {t("configurator.version")}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              v{APP_VERSION}
            </Typography>
          </Box>

          <Divider sx={{ ...configurator.divider, my: 2.5 }} />

          <AppButton
            variant="outlined"
            color="error"
            fullWidth
            startIcon={<LogoutOutlinedIcon />}
            busyIgnore
            onClick={() => {
              handleCloseConfigurator();
              navigate("/logout");
            }}
          >
            {t("navbar.logout")}
          </AppButton>
        </Box>
      </ConfiguratorRoot>
    </>
  );
}

export default Configurator;
